import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { navigation } from '@/mock/data';

const Logo = () => (
  <Link to="/" className="flex items-center gap-2 group" aria-label="Finirio home">
    <img
      src="https://customer-assets.emergentagent.com/job_site-creator-3034/artifacts/pbfdilsv_Finirio.JPG"
      alt="Finirio - Empowering Your Financial Future"
      className="h-11 md:h-12 w-auto object-contain"
      loading="eager"
      width="200"
      height="48"
    />
  </Link>
);

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMobileGroup, setOpenMobileGroup] = useState(null);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-[#0b2545]/10 shadow-[0_1px_0_rgba(11,37,69,.04),0_8px_24px_-16px_rgba(11,37,69,.18)]">
      <div className="max-w-[1240px] mx-auto px-4 lg:px-6 h-[72px] flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          {navigation.map((item) => (
            <div key={item.label} className="relative group">
              {item.external ? (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-2 rounded-md text-[14.5px] font-semibold text-slate-700 hover:text-[#1e73be] transition-colors"
                >
                  {item.label}
                </a>
              ) : (
              <Link
                to={item.href}
                className={`relative flex items-center gap-1 px-3 py-2 rounded-md text-[14.5px] font-semibold text-slate-700 hover:text-[#1e73be] transition-colors after:absolute after:left-3 after:right-3 after:-bottom-[3px] after:h-[2px] after:rounded-full after:bg-gradient-to-r after:from-[#c9a24b] after:to-[#e8c887] after:origin-left after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 ${
                  location.pathname === item.href ? 'text-[#1e73be] after:scale-x-100' : ''
                }`}
              >
                {item.label}
                 {item.children && <ChevronDown className="w-4 h-4 mt-[1px] transition-transform group-hover:rotate-180 group-focus-within:rotate-180" />}
              </Link>
              )}
              {item.children && (
                 <div className="absolute left-0 top-full pt-3 opacity-0 translate-y-1 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:translate-y-0 group-focus-within:pointer-events-auto transition-all duration-200">
                  <div className="min-w-[230px] rounded-xl bg-white shadow-[0_18px_44px_-16px_rgba(11,37,69,.3)] border border-[#0b2545]/8 py-2 overflow-hidden">
                    <span className="block h-[2px] -mt-2 mb-2" style={{ background: 'linear-gradient(90deg,#c9a24b,#e8c887,#c9a24b)' }} />
                     {item.children.map((c) => c.external ? (
                       <a key={c.label} href={c.href} target="_blank" rel="noopener noreferrer"
                         className="block px-4 py-2.5 text-[14px] font-medium text-slate-700 hover:bg-[#f4f9ff] hover:text-[#1e73be] hover:pl-5 transition-all">
                         {c.label}
                       </a>
                     ) : (
                       <Link key={c.label} to={c.href}
                         className="block px-4 py-2.5 text-[14px] font-medium text-slate-700 hover:bg-[#f4f9ff] hover:text-[#1e73be] hover:pl-5 transition-all">
                         {c.label}
                       </Link>
                     ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <Link to="/apply-now-cbi" className="btn-lux ml-3 !px-5 !py-2.5 !text-[14px]">
            Apply Now
          </Link>
        </nav>

        <div className="lg:hidden flex items-center gap-2">
          <Link to="/apply-now-cbi" className="btn-lux !px-3.5 !py-2 !text-[13px]">
            Apply
          </Link>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="p-2 rounded-md text-slate-700 hover:bg-slate-100"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-100 bg-white">
          <div className="px-4 py-3 space-y-1">
             {navigation.map((item) => (
               <div key={item.label}>
                 <div className="flex items-center text-slate-700 font-medium">
                   {item.external ? (
                     <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)}
                       className="flex-1 px-3 py-2">{item.label}</a>
                   ) : (
                     <Link to={item.href} onClick={() => setMobileOpen(false)}
                       className="flex-1 px-3 py-2">{item.label}</Link>
                   )}
                   {item.children && (
                     <button type="button"
                       aria-label={`${item.label} menu`}
                       aria-expanded={openMobileGroup === item.label}
                       onClick={() => setOpenMobileGroup(openMobileGroup === item.label ? null : item.label)}
                       className="p-2 mr-2 rounded hover:bg-slate-100">
                       <ChevronDown className={`w-4 h-4 transition-transform ${openMobileGroup === item.label ? 'rotate-180' : ''}`} />
                     </button>
                   )}
                 </div>
                 {item.children && openMobileGroup === item.label && (
                   <div className="pl-6">
                     {item.children.map((c) => c.external ? (
                       <a key={c.label} href={c.href} target="_blank" rel="noopener noreferrer"
                         onClick={() => setMobileOpen(false)}
                         className="block px-3 py-2 text-[14px] text-slate-600 hover:text-[#1e73be]">{c.label}</a>
                     ) : (
                       <Link key={c.label} to={c.href} onClick={() => setMobileOpen(false)}
                         className="block px-3 py-2 text-[14px] text-slate-600 hover:text-[#1e73be]">{c.label}</Link>
                     ))}
                   </div>
                 )}
               </div>
             ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
